import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import "./CampaignDetail.css";
import ImportInvitationsModal from "./ImportInvitationsModal";
import { FiSearch, FiArrowLeft, FiUpload, FiSend, FiBell, FiFeather } from "react-icons/fi";
import { toast } from "react-toastify";

const API = process.env.REACT_APP_API_URL;

const uniq = (arr) => [...new Set(arr.filter(Boolean))];

// Traducciones
const STATUS_LABELS = {
  created: "Creado",
  sent_to_sign: "Enviado a firma",
  signed: "Firmado",
  failed: "Fallido"
};

const SIGN_LABELS = {
  pending: "Pendiente",
  sent_to_sign: "En espera",
  signed: "Firmado",
  failed: "Fallido",
  expired: "Expirado"
};

function CampaignDetail() {
  const { campaignId } = useParams();
  const navigate = useNavigate();
  const [campaign, setCampaign] = useState(null);
  const [invitations, setInvitations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showImport, setShowImport] = useState(false);
  const [showConfirmSend, setShowConfirmSend] = useState(false);
  const [sending, setSending] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [showValidateModal, setShowValidateModal] = useState(false);
  const [validating, setValidating] = useState(false);
  const [validationResult, setValidationResult] = useState(null);
  const [showMassResendModal, setShowMassResendModal] = useState(false);
  const [massLoading, setMassLoading] = useState(false);
  const [massResult, setMassResult] = useState(null);


  // ================= FILTROS =================
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("");        // created/invited/accepted...
  const [filterSign, setFilterSign] = useState("");            // not_required/pending/sent_to_sign...

  const statusOptions = useMemo(
    () => uniq(invitations.map((i) => i.status)),
    [invitations]
  );

  const signOptions = useMemo(
    () => uniq(invitations.map((i) => i.signatureStatus)),
    [invitations]
  );

  // ================= PAGINACIÓN =================
  const [page, setPage] = useState(1);
  const limit = 10;

  // ===============================
  // Cargar campaña + invitaciones
  // ===============================
  const loadData = async () => {
    try {
      setLoading(true);

      const [cRes, iRes] = await Promise.all([
        axios.get(`${API}/api/campaigns/${campaignId}`),
        axios.get(`${API}/api/campaigns/${campaignId}/invitations`)
      ]);

      setCampaign(cRes.data);
      setInvitations(iRes.data || []);
    } catch (err) {
      console.error("Error cargando campaña", err);
    } finally {
      setLoading(false);
    }
  };

  // ===============================
  // Enviar invitaciones
  // ===============================
  const sendInvitations = async () => {
    try {
      setSending(true);
      await axios.post(`${API}/api/campaigns/${campaignId}/invitations/send`);
      await loadData();
    } catch (err) {
      console.error("Error enviando invitaciones", err);
    } finally {
      setSending(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [campaignId]);

  const norm = (v) =>
    String(v || "")
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .trim();

  const matchesSearch = (inv) => {
    const q = norm(search);
    if (!q) return true;

    const doc = norm(inv.numeroDocumento);
    const name = norm(inv.nombres);
    return doc.includes(q) || name.includes(q);
  };

  // ================= FILTRADO =================
  const filteredInvitations = useMemo(() => {
    return invitations.filter((inv) => {
      if (!matchesSearch(inv)) return false;

      if (filterStatus && inv.status !== filterStatus) return false;

      if (filterSign && inv.signatureStatus !== filterSign) return false;

      return true;
    });
  }, [invitations, search, filterStatus, filterSign]);

  // ================= PAGINACIÓN (sobre filtrados) =================
  const total = filteredInvitations.length;
  const pages = Math.ceil(total / limit) || 1;

  const startIndex = (page - 1) * limit;
  const endIndex = startIndex + limit;

  const paginatedInvitations = filteredInvitations.slice(startIndex, endIndex);

  // Reset página si cambia el total
  useEffect(() => {
    if (page > pages) setPage(1);
  }, [pages, page]);


  if (loading) return <p>Cargando campaña...</p>;
  if (!campaign) return <p>Campaña no encontrada</p>;

  const executeConfirmAction = async () => {
    if (!confirmAction) return;

    try {
      if (confirmAction.type === "resend_signature") {
        await handleResendSignature(confirmAction.invitation._id);
      }

      if (confirmAction.type === "validate_signature") {
        await handleValidateSingle(confirmAction.invitation._id);
      }

      setConfirmAction(null);
    } catch (err) {
      console.error(err);
    }
  };

  // ================= ACCIONES POR FILA =================

  // Reenviar firma EnvíaFirma
  const handleResendSignature = async (invitationId) => {
    try {
      await axios.post(
        `${API}/api/campaigns/invitations/${invitationId}/resend`
      );

      toast.success("Notificación de firma reenviada correctamente");

      await loadData();
    } catch (err) {
      console.error("Error reenviando firma", err);

      toast.error(
        err?.response?.data?.message ||
        "Error al reenviar la notificación"
      );
    }
  };

  // Validar estado de firma
  const handleValidateSingle = async (invitationId) => {
    try {
      const res = await axios.post(
        `${API}/api/campaigns/invitations/${invitationId}/validate`
      );

      if (res.data?.signed) {
        toast.success("El documento ya fue firmado");
      } else {
        toast.info("El documento aún está pendiente de firma");
      }

      await loadData();
    } catch (err) {
      console.error("Error validando firma", err);

      toast.error(
        err?.response?.data?.message ||
        "Error validando el estado de la firma"
      );
    }
  };

  const isClosed = campaign.status === "closed";

  // Hay registros cargados
  const hasInvitations = invitations.length > 0;

  // La campaña ya fue enviada a EnvíaFirma
  const campaignSent = invitations.some(
    (i) =>
      i.status === "sent" ||
      i.signatureStatus === "sent_to_sign" ||
      i.signatureStatus === "signed"
  );

  const handleValidateSignatures = async () => {
    try {
      setValidating(true);
      setValidationResult(null);

      const res = await axios.post(
        `${API}/api/campaigns/${campaignId}/validate-signatures`
      );

      setValidationResult(res.data);
    } catch (err) {
      console.error("Error validando firmas", err);
      setValidationResult({ error: true });
    } finally {
      setValidating(false);
    }
  };

  return (
    <div className="cam-detail-root">

      {/* ================= HEADER ================= */}
      <header className="cam-detail-header">
        <div className="cam-detail-header-left">
          <FiArrowLeft
            className="cam-detail-back"
            onClick={() => navigate("/campaigns")}
          />

          <h2 className="cam-detail-title">
            | {campaign.name}
            {isClosed && (
              <span className="cam-detail-badge-closed">
                {" "}-{" "} Cerrada
              </span>
            )}
          </h2>
        </div>

        {!isClosed && (
          <div className="cam-detail-actions">

            <button
              className="cam-detail-btn-secondary"
              onClick={() => setShowImport(true)}
              disabled={sending}
            >
              <FiUpload size={15} /> Importar base
            </button>

            {hasInvitations && !campaignSent && (
              <button
                className="cam-detail-btn-primary"
                onClick={() => setShowConfirmSend(true)}
                disabled={sending}
              >
                <FiSend size={15} />
                {sending ? "Enviando..." : "Enviar campaña"}
              </button>
            )}

            {campaignSent && (
              <button
                className="cam-detail-btn-primary"
                onClick={() => setShowValidateModal(true)}
              >
                <FiFeather size={15} />
                Validar firmas
              </button>
            )}

            {campaignSent && (
              <button
                className="cam-detail-btn-primary"
                onClick={() => setShowMassResendModal(true)}
              >
                <FiBell size={15} />
                Reenviar notificaciones
              </button>
            )}

          </div>
        )}
      </header>

      {/* ================= SUMMARY ================= */}
      <div className="cam-detail-summary">
        {[
          {
            label: "Invitados",
            value: Number(campaign.stats?.total || 0).toLocaleString("es-CO"),
          },
          {
            label: "Firmaron",
            value: Number(campaign.stats?.signed || 0).toLocaleString("es-CO"),
          },
        ].map((item, idx) => (
          <React.Fragment key={idx}>
            <div className="cam-detail-summary-block">
              <span className="cam-detail-summary-label">{item.label}</span>
              <h2>{item.value}</h2>
            </div>
            {idx < 1 && <div className="cam-detail-summary-divider" />}
          </React.Fragment>
        ))}
      </div>

      {/* ================= FILTROS (ESTILO AFILIACIONES) ================= */}
      <div className="inv-filters">
        <div className="inv-row">

          {/* 🔍 Buscador */}
          <div className="campa-field">
            <FiSearch />
            <input
              placeholder="Buscar por documento o nombre…"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
            />
          </div>

          {/* Estado */}
          <select
            className="inv-select"
            value={filterStatus}
            onChange={(e) => { setFilterStatus(e.target.value); setPage(1); }}
          >
            <option value="">Todos los estados</option>
            {statusOptions.map((s) => (
              <option key={s} value={s}>
                {STATUS_LABELS[s] || s}
              </option>
            ))}
          </select>

          {/* Firma */}
          <select
            className="inv-select"
            value={filterSign}
            onChange={(e) => { setFilterSign(e.target.value); setPage(1); }}
          >
            <option value="">Todas las firmas</option>
            {signOptions.map((s) => (
              <option key={s} value={s}>
                {SIGN_LABELS[s] || s}
              </option>
            ))}
          </select>

          {/* 🔄 Restablecer */}
          <button
            className="inv-btn-ghost"
            onClick={() => {
              setSearch("");
              setFilterStatus("");
              setFilterSign("");
              setPage(1);
            }}
            disabled={!search && !filterStatus && !filterSign}
          >
            Restablecer
          </button>

          <div style={{ marginTop: 0, fontSize: "0.85rem", color: "#666" }}>
            Mostrando <b>{filteredInvitations.length}</b> de <b>{invitations.length}</b>
          </div>
        </div>
      </div>

      {/* ================= TABLE ================= */}
      <div className="cam-detail-table">
        <table>
          <thead>
            <tr>
              <th>Identificación</th>
              <th>Nombre</th>
              <th>Celular</th>
              <th>Empresa</th>
              <th>Ciudad</th>
              <th>Estado Invitación</th>
              <th>Estado Firma</th>
              <th>Fecha</th>
              <th>Acciones</th>
            </tr>
          </thead>

          <tbody>
            {paginatedInvitations.length === 0 && (
              <tr>
                <td colSpan="9" className="cam-detail-empty">
                  No hay invitados en esta campaña
                </td>
              </tr>
            )}

            {paginatedInvitations.map((inv) => (
              <tr key={inv._id}>
                <td>{inv.numeroDocumento}</td>
                <td>{inv.nombres}</td>
                <td>{inv.celular}</td>
                <td>{inv.empresaContratante}</td>
                <td>{inv.municipio}</td>

                {/* ===== ESTADO ===== */}
                <td>
                  <span className={`cam-detail-chip cam-detail-status-${inv.status}`}>
                    {STATUS_LABELS[inv.status] || inv.status}
                  </span>
                </td>

                {/* ===== FIRMA ===== */}
                <td>
                  <span
                    className={`cam-detail-chip cam-detail-sign-${inv.signatureStatus}`}
                  >
                    {SIGN_LABELS[inv.signatureStatus] || inv.signatureStatus}
                  </span>
                </td>

                {/* ===== FECHA ===== */}
                <td>
                  {inv.createdAt
                    ? new Date(inv.createdAt).toLocaleDateString()
                    : "-"}
                </td>

                {/* ===== ACCIONES ===== */}
                <td className="cam-detail-actions-cell">

                  {/* Reenviar firma */}
                  <button
                    className={`inv-icon-btn ${inv.signatureStatus === "signed"
                      ? "cam-btn-disabled"
                      : ""
                      }`}
                    disabled={
                      isClosed ||
                      !inv.externalSignatureId ||
                      inv.signatureStatus === "signed"
                    }
                    title={
                      !inv.externalSignatureId
                        ? "La campaña aún no ha sido enviada"
                        : inv.signatureStatus === "signed"
                          ? "Documento firmado"
                          : "Reenviar notificación"
                    }
                    onClick={() =>
                      setConfirmAction({
                        type: "resend_signature",
                        invitation: inv
                      })
                    }
                  >
                    <FiBell size={15} />
                  </button>

                  {/* Validar firma */}
                  <button
                    className={`inv-icon-btn ${inv.signatureStatus === "signed"
                      ? "cam-btn-disabled"
                      : ""
                      }`}
                    disabled={
                      isClosed ||
                      !inv.externalSignatureId ||
                      inv.signatureStatus === "signed"
                    }
                    title={
                      !inv.externalSignatureId
                        ? "La campaña aún no ha sido enviada"
                        : inv.signatureStatus === "signed"
                          ? "Documento firmado"
                          : "Validar estado firma"
                    }
                    onClick={() =>
                      setConfirmAction({
                        type: "validate_signature",
                        invitation: inv
                      })
                    }
                  >
                    <FiFeather size={15} />
                  </button>

                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* ================= PAGINACIÓN ================= */}
        <div className="inv-paginacion">
          <button disabled={page === 1} onClick={() => setPage(page - 1)}>
            Anterior
          </button>

          <span className="inv-pagina-info">
            Página {page} de {pages}
          </span>

          <button
            disabled={page === pages}
            onClick={() => setPage(page + 1)}
          >
            Siguiente
          </button>
        </div>
      </div>

      {/* ================= MODAL IMPORTAR ================= */}
      {showImport && (
        <ImportInvitationsModal
          campaignId={campaignId}
          onClose={() => setShowImport(false)}
          onImported={loadData}
        />
      )}

      {/* ================= MODAL CONFIRMAR ENVÍO ================= */}
      {showConfirmSend && (
        <div className="cam-detail-modal-overlay">
          <div className="cam-detail-modal">
            <h3 style={{ textAlign: "center", marginBottom: 14 }}>
              Confirmar Envío
            </h3>

            <p style={{ textAlign: "center" }}>
              ¿Deseas enviar invitaciones a todos los invitados pendientes?
            </p>

            <div
              style={{
                display: "flex",
                justifyContent: "center",
                gap: 16,
                marginTop: 22
              }}
            >
              <button
                className="cam-detail-btn-secondary"
                onClick={() => setShowConfirmSend(false)}
              >
                Cancelar
              </button>

              <button
                className="cam-detail-btn-primary"
                onClick={async () => {
                  setShowConfirmSend(false);
                  await sendInvitations();
                }}
              >
                Confirmar envío
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ================= MODAL ACCIONES POR FILA ================= */}
      {confirmAction && (
        <div className="camp-modal-overlay">
          <div className="cam-detail-modal">
            <h3 style={{ textAlign: "center", marginBottom: 10 }}>
              {confirmAction.type === "resend_signature" &&
                "Reenviar Notificación"}

              {confirmAction.type === "validate_signature" &&
                "Validar Firma"}
            </h3>

            <p style={{ textAlign: "center" }}>
              {confirmAction.type === "resend_signature" &&
                "¿Deseas reenviar la notificación de firma al empleado?"}

              {confirmAction.type === "validate_signature" &&
                "¿Deseas consultar el estado actual de la firma?"}
            </p>

            <div
              style={{
                display: "flex",
                justifyContent: "center",
                gap: 14,
                marginTop: 24
              }}
            >
              <button
                className="cam-detail-btn-secondary"
                onClick={() => setConfirmAction(null)}
              >
                Cancelar
              </button>

              <button
                className="cam-detail-btn-primary"
                onClick={executeConfirmAction}
              >
                Continuar
              </button>
            </div>
          </div>
        </div>
      )}

      {showValidateModal && (
        <div className="camp-modal-overlay">
          <div className="cam-detail-modal">
            <h3 style={{ textAlign: "center", marginBottom: 14 }}>
              Validación de Firmas
            </h3>

            {!validationResult && (
              <p style={{ textAlign: "center" }}>
                ¿Deseas validar las firmas de esta campaña?
              </p>
            )}

            {validationResult && !validationResult.error && (
              <div style={{ textAlign: "center", marginTop: 10 }}>
                <p><strong>Validación completada</strong></p>
                <p>Revisadas: {validationResult.checked}</p>
                <p>Firmadas nuevas: {validationResult.signed}</p>
                <p>Omitidas: {validationResult.skipped}</p>
              </div>
            )}

            {validationResult?.error && (
              <p style={{ color: "red", textAlign: "center" }}>
                Error validando firmas
              </p>
            )}

            <div
              style={{
                display: "flex",
                justifyContent: "center",
                gap: 16,
                marginTop: 22
              }}
            >
              <button
                className="cam-detail-btn-secondary"
                onClick={() => {
                  setShowValidateModal(false);
                  setValidationResult(null);
                  loadData();
                }}
                disabled={validating}
              >
                Cerrar
              </button>

              {!validationResult && (
                <button
                  className="cam-detail-btn-primary"
                  onClick={handleValidateSignatures}
                  disabled={validating}
                >
                  {validating ? "Validando..." : "Confirmar"}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {showMassResendModal && (
        <div className="camp-modal-overlay">
          <div className="cam-detail-modal">
            <h3 style={{ textAlign: "center", marginBottom: 14 }}>
              Reenvío de Notificaciones
            </h3>

            {!massResult && (
              <p style={{ textAlign: "center", marginBottom: 12 }}>
                Se reenviará la notificación de firma a todos los empleados que aún no han firmado.
              </p>
            )}

            {/* ✅ RESUMEN */}
            {massResult && (
              <div style={{ marginTop: 14, textAlign: "center" }}>
                <p style={{ marginBottom: 6 }}>
                  <b>Resultado:</b> Reenvío de firmas
                </p>
                <p><b>Procesados:</b> {massResult.processed}</p>
                <p><b>Enviados:</b> {massResult.sent}</p>
                <p><b>Omitidos:</b> {massResult.skipped}</p>
                <p><b>Errores:</b> {massResult.errors}</p>
              </div>
            )}

            {/* ✅ ACCIONES */}
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                gap: 14,
                marginTop: 22
              }}
            >
              <button
                className="cam-detail-btn-secondary"
                onClick={() => {
                  setShowMassResendModal(false);
                  setMassResult(null);
                }}
                disabled={massLoading}
              >
                Cerrar
              </button>

              {!massResult && (
                <button
                  className="cam-detail-btn-primary"
                  disabled={massLoading || isClosed}
                  onClick={async () => {
                    try {
                      setMassLoading(true);
                      setMassResult(null);

                      const res = await axios.post(
                        `${API}/api/campaigns/${campaignId}/notifications/resend`,
                        { type: "signature" }
                      );

                      setMassResult(res.data);
                      await loadData();
                    } catch (err) {
                      console.error(err);
                      setMassResult({
                        processed: 0,
                        sent: 0,
                        skipped: 0,
                        errors: 1
                      });
                    } finally {
                      setMassLoading(false);
                    }
                  }}
                >
                  {massLoading ? "Reenviando..." : "Reenviar"}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default CampaignDetail;
